export const SITE_EXPERIENCE_DEFAULTS = Object.freeze({
  generic: Object.freeze({
    navigation: Object.freeze({ productsLabel: "Productos", productsUrl: "/productos", allProductsLabel: "Ver todos" }),
    hero: Object.freeze({ secondaryCtaLabel: "Ver productos", secondaryCtaUrl: "/productos" }),
    productsPage: Object.freeze({
      productsTitle: "Productos",
      hybridTitle: "Productos + servicio",
      loadingText: "Cargando catálogo…",
      searchPlaceholder: "Buscar por nombre, servicio, código, categoría o subcategoría",
      allTypesLabel: "Productos y servicios",
      emptyTitle: "No encontramos resultados",
      emptyText: "Probá con otra búsqueda o quitá los filtros activos.",
      emptyResetLabel: "Ver todo el catálogo",
    }),
    productPage: Object.freeze({ backLabel: "Volver al catálogo" }),
    featured: Object.freeze({
      title: "Productos destacados",
      subtitle: "Conocé las últimas novedades del catálogo.",
      loadingText: "Cargando productos…",
      emptyText: "Todavía no hay productos para mostrar.",
      errorText: "No se pudieron cargar los productos.",
    }),
    categories: Object.freeze({
      title: "Explorá por categorías",
      loadingText: "Cargando categorías…",
      emptyText: "Todavía no hay categorías para mostrar.",
      errorText: "No se pudieron cargar las categorías.",
      ctaLabel: "Ver productos",
      productSingular: "producto",
      productPlural: "productos",
    }),
    brands: Object.freeze({
      subtitle: "Elegí una marca y explorá el catálogo disponible.",
      ctaLabel: "Ver productos",
    }),
    about: Object.freeze({
      eyebrow: "Sobre nuestro negocio",
      title: "Conocé nuestro negocio",
      description: "Trabajamos para ofrecer una atención cercana, soluciones claras y una experiencia confiable en cada consulta.",
    }),
    contact: Object.freeze({ title: "Contacto" }),
    footer: Object.freeze({ productsLabel: "Productos" }),
  }),
  gastronomic: Object.freeze({
    navigation: Object.freeze({ productsLabel: "Carta", productsUrl: "/carta", allProductsLabel: "Ver carta" }),
    hero: Object.freeze({ secondaryCtaLabel: "Ver carta", secondaryCtaUrl: "/carta" }),
    productsPage: Object.freeze({
      productsTitle: "Carta",
      hybridTitle: "Carta y servicios",
      loadingText: "Cargando carta…",
      searchPlaceholder: "Buscar en la carta por nombre, categoría o subcategoría",
      allTypesLabel: "Carta y servicios",
      emptyTitle: "La carta todavía está vacía",
      emptyText: "Las opciones aparecen acá cuando el negocio las incorpora a la carta.",
      emptyResetLabel: "Ver toda la carta",
    }),
    productPage: Object.freeze({ backLabel: "Volver a la carta" }),
    featured: Object.freeze({
      title: "Destacados de la carta",
      subtitle: "Conocé los destacados de nuestra carta.",
      loadingText: "Cargando destacados…",
      emptyText: "Todavía no hay destacados para mostrar en la carta.",
      errorText: "No se pudieron cargar los destacados de la carta.",
    }),
    categories: Object.freeze({
      title: "Explorá nuestra carta",
      loadingText: "Cargando secciones de la carta…",
      emptyText: "Todavía no hay secciones de la carta para mostrar.",
      errorText: "No se pudieron cargar las secciones de la carta.",
      ctaLabel: "Ver en la carta",
      productSingular: "opción",
      productPlural: "opciones",
    }),
    brands: Object.freeze({
      subtitle: "Elegí una marca y explorá las opciones disponibles.",
      ctaLabel: "Ver opciones",
    }),
    about: Object.freeze({
      eyebrow: "Nuestra propuesta",
      title: "Conocé nuestra propuesta",
      description: "Conocé más sobre el negocio y la propuesta que compartimos en nuestra carta.",
    }),
    contact: Object.freeze({ title: "Ubicación, horarios y contacto" }),
    footer: Object.freeze({ productsLabel: "Carta" }),
  }),
});

const PATHS = Object.freeze({
  navigationProductsLabel: "navigation.productsLabel",
  navigationProductsUrl: "navigation.productsUrl",
  navigationAllProductsLabel: "navigation.allProductsLabel",
  heroSecondaryLabel: "hero.secondaryCtaLabel",
  heroSecondaryUrl: "hero.secondaryCtaUrl",
  footerProductsLabel: "footer.productsLabel",
});

const LEGACY_SYSTEM_DEFAULTS = Object.freeze({
  "catalog.featured.title": Object.freeze(["Platos destacados"]),
  "catalog.featured.subtitle": Object.freeze(["Descubrí propuestas de nuestra carta."]),
  "catalog.featured.loadingText": Object.freeze(["Cargando platos…"]),
  "catalog.featured.emptyText": Object.freeze(["Todavía no hay opciones en la carta para mostrar."]),
  "catalog.featured.errorText": Object.freeze(["No se pudieron cargar los platos."]),
});

export const EXPERIENCE_DEFAULT_OVERRIDE_PATHS = Object.freeze([
  PATHS.navigationProductsLabel,
  PATHS.navigationProductsUrl,
  PATHS.navigationAllProductsLabel,
  PATHS.heroSecondaryLabel,
  PATHS.heroSecondaryUrl,
  PATHS.footerProductsLabel,
  ...Object.keys(SITE_EXPERIENCE_DEFAULTS.generic.productsPage).map((key) => `catalog.productsPage.${key}`),
  ...Object.keys(SITE_EXPERIENCE_DEFAULTS.generic.productPage).map((key) => `catalog.productPage.${key}`),
  ...Object.keys(SITE_EXPERIENCE_DEFAULTS.generic.featured).map((key) => `catalog.featured.${key}`),
  ...Object.keys(SITE_EXPERIENCE_DEFAULTS.generic.categories).map((key) => `catalog.categories.${key}`),
  ...Object.keys(SITE_EXPERIENCE_DEFAULTS.generic.brands).map((key) => `catalog.brands.${key}`),
  ...Object.keys(SITE_EXPERIENCE_DEFAULTS.generic.about).map((key) => `about.${key}`),
  ...Object.keys(SITE_EXPERIENCE_DEFAULTS.generic.contact).map((key) => `contact.${key}`),
]);

const OVERRIDE_PATH_SET = new Set(EXPERIENCE_DEFAULT_OVERRIDE_PATHS);

function getAtPath(source, path) {
  return String(path || "").split(".").reduce((value, key) => value?.[key], source);
}

function setAtPath(target, path, value) {
  const parts = String(path || "").split(".");
  let cursor = target;
  for (let index = 0; index < parts.length - 1; index += 1) {
    const key = parts[index];
    cursor[key] = { ...(cursor[key] || {}) };
    cursor = cursor[key];
  }
  cursor[parts[parts.length - 1]] = value;
}

function overrideSet(settings = {}) {
  return new Set(
    (Array.isArray(settings?.experienceDefaults?.overrides) ? settings.experienceDefaults.overrides : [])
      .filter((path) => OVERRIDE_PATH_SET.has(path)),
  );
}

export function isExperienceDefaultOverridePath(path) {
  return OVERRIDE_PATH_SET.has(String(path || ""));
}

export function withExperienceDefaultOverrides(settings = {}, changes = {}) {
  const overrides = overrideSet(settings);
  Object.entries(changes || {}).forEach(([path, value]) => {
    if (!OVERRIDE_PATH_SET.has(path)) return;
    if (String(value ?? "").trim()) overrides.add(path);
    else overrides.delete(path);
  });
  return {
    ...settings,
    experienceDefaults: {
      ...(settings.experienceDefaults || {}),
      overrides: [...overrides].sort(),
    },
  };
}

function dynamicGenericDefault(path, source, fallback) {
  if (path !== "about.title") return fallback;
  const businessName = String(source?.business?.name || "").trim();
  return businessName ? `Conocé ${businessName}` : fallback;
}

function recognized(value, genericDefault, gastronomicDefault, path, source) {
  const normalized = value == null ? "" : String(value);
  if (normalized === "" || normalized === genericDefault || normalized === gastronomicDefault) return true;
  if (normalized === dynamicGenericDefault(path, source, genericDefault)) return true;
  return (LEGACY_SYSTEM_DEFAULTS[path] || []).includes(normalized);
}

function adaptPath(resolved, source, overrides, path, genericDefault, gastronomicDefault, targetDefault) {
  if (overrides.has(path)) return;
  if (!recognized(getAtPath(source, path), genericDefault, gastronomicDefault, path, source)) return;
  setAtPath(resolved, path, targetDefault);
}

function targetValue(path, targetDefault, source, gastronomicMenuEnabled) {
  return gastronomicMenuEnabled ? targetDefault : dynamicGenericDefault(path, source, targetDefault);
}

function resolveExperience(settings, { gastronomicMenuEnabled = false, productsEnabled = true } = {}) {
  if (!productsEnabled) return settings;

  const generic = SITE_EXPERIENCE_DEFAULTS.generic;
  const gastronomic = SITE_EXPERIENCE_DEFAULTS.gastronomic;
  const target = gastronomicMenuEnabled ? gastronomic : generic;
  const overrides = overrideSet(settings);
  const resolved = {
    ...settings,
    experienceDefaults: { ...(settings.experienceDefaults || {}), overrides: [...overrides] },
    navigation: { ...(settings.navigation || {}) },
    hero: { ...(settings.hero || {}) },
    catalog: {
      ...(settings.catalog || {}),
      productsPage: { ...(settings.catalog?.productsPage || {}) },
      productPage: { ...(settings.catalog?.productPage || {}) },
      featured: { ...(settings.catalog?.featured || {}) },
      categories: { ...(settings.catalog?.categories || {}) },
      brands: { ...(settings.catalog?.brands || {}) },
    },
    about: { ...(settings.about || {}) },
    contact: { ...(settings.contact || {}) },
    footer: { ...(settings.footer || {}) },
  };

  const navigationPairOverridden = overrides.has(PATHS.navigationProductsLabel) || overrides.has(PATHS.navigationProductsUrl);
  const navigationPairRecognized =
    recognized(settings.navigation?.productsLabel, generic.navigation.productsLabel, gastronomic.navigation.productsLabel, PATHS.navigationProductsLabel, settings) &&
    recognized(settings.navigation?.productsUrl, generic.navigation.productsUrl, gastronomic.navigation.productsUrl, PATHS.navigationProductsUrl, settings);
  const automaticCatalogNavigation = !navigationPairOverridden && navigationPairRecognized;

  if (automaticCatalogNavigation) {
    resolved.navigation.productsLabel = target.navigation.productsLabel;
    resolved.navigation.productsUrl = target.navigation.productsUrl;
    adaptPath(resolved, settings, overrides, PATHS.navigationAllProductsLabel, generic.navigation.allProductsLabel, gastronomic.navigation.allProductsLabel, target.navigation.allProductsLabel);
    adaptPath(resolved, settings, overrides, PATHS.footerProductsLabel, generic.footer.productsLabel, gastronomic.footer.productsLabel, target.footer.productsLabel);
  }

  const heroPairOverridden = overrides.has(PATHS.heroSecondaryLabel) || overrides.has(PATHS.heroSecondaryUrl);
  const heroPairRecognized =
    recognized(settings.hero?.secondaryCtaLabel, generic.hero.secondaryCtaLabel, gastronomic.hero.secondaryCtaLabel, PATHS.heroSecondaryLabel, settings) &&
    recognized(settings.hero?.secondaryCtaUrl, generic.hero.secondaryCtaUrl, gastronomic.hero.secondaryCtaUrl, PATHS.heroSecondaryUrl, settings);
  if (!heroPairOverridden && heroPairRecognized) {
    resolved.hero.secondaryCtaLabel = target.hero.secondaryCtaLabel;
    resolved.hero.secondaryCtaUrl = target.hero.secondaryCtaUrl;
  }

  for (const section of ["featured", "categories", "brands"]) {
    Object.entries(generic[section]).forEach(([key, genericDefault]) => {
      const path = `catalog.${section}.${key}`;
      adaptPath(resolved, settings, overrides, path, genericDefault, gastronomic[section][key], targetValue(path, target[section][key], settings, gastronomicMenuEnabled));
    });
  }

  for (const section of ["about", "contact"]) {
    Object.entries(generic[section]).forEach(([key, genericDefault]) => {
      const path = `${section}.${key}`;
      adaptPath(resolved, settings, overrides, path, genericDefault, gastronomic[section][key], targetValue(path, target[section][key], settings, gastronomicMenuEnabled));
    });
  }

  if (automaticCatalogNavigation) {
    for (const section of ["productsPage", "productPage"]) {
      Object.entries(generic[section]).forEach(([key, genericDefault]) => {
        const path = `catalog.${section}.${key}`;
        adaptPath(resolved, settings, overrides, path, genericDefault, gastronomic[section][key], targetValue(path, target[section][key], settings, gastronomicMenuEnabled));
      });
    }
  }

  return resolved;
}

export function resolveSiteExperienceDefaults(settings = {}, options = {}) {
  return resolveExperience(settings, options);
}
